import { ClampedText } from '../../../components/ClampedText'
import { Check, ChevronDown, Copy, Crown, Edit3, Layers, Link2, MoreVertical, Pause, Play, Search, Sparkles, Trash2, Unlink, Wand2 } from 'lucide-react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { Link } from 'react-router-dom'
import type { SunoPrompt, SunoPromptPool } from '../../../api/suno'
import { IconButton, Pill, RowActions } from '../../../components/ui'
import type { PlayableTrack } from '../../audio/useAudioPlayer'
import styles from '../../../pages/ToolsPage.module.css'

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

export type PromptActionMenuPosition = {
  left: number
  placement: 'above' | 'below'
  top: number
}

type PromptLibraryItemOptions = {
  isPoolVariant?: boolean | undefined
  pool?: SunoPromptPool | undefined
}

type PromptLibraryItemActions = {
  onCopyText: (value: string, label: string) => Promise<void>
  onDeletePrompt: (prompt: SunoPrompt) => void
  onDissolvePromptPool: (poolId: string) => Promise<void>
  onEditPrompt: (prompt: SunoPrompt) => void
  onOpenPromptPoolEditor: (poolId: string) => Promise<void>
  onOpenSimilarity: (prompt: SunoPrompt) => void
  onOpenTracks: (prompt: SunoPrompt) => void
  onPlayPromptTracks: (prompt: SunoPrompt) => Promise<void>
  onRemovePromptPoolVariant: (pool: SunoPromptPool, promptId: string) => Promise<void>
  onSetPromptPoolCanonical: (pool: SunoPromptPool, promptId: string) => Promise<void>
  onTogglePromptPoolExpansion: (poolId: string, anchorPromptId: string) => void
}

type PromptLibraryPlaybackState = {
  audioLoading: boolean
  audioPlaying: boolean
  loadingPromptId: string | null
  playbackPromptId: string | null
  playbackTracks: PlayableTrack[]
  playingTrack: PlayableTrack | null
}

type PromptLibraryTableRowProps = PromptLibraryItemActions & PromptLibraryPlaybackState & PromptLibraryItemOptions & {
  actionMenuId: string | null
  actionMenuPosition: PromptActionMenuPosition | null
  isCurrentPromptVariant?: boolean | undefined
  isExpanded?: boolean | undefined
  onCloseActionMenu: () => void
  onToggleActionMenu: (event: ReactMouseEvent<HTMLButtonElement>, menuId: string) => void
  prompt: SunoPrompt
}

const isPromptPlaybackLoading = (prompt: SunoPrompt, playback: PromptLibraryPlaybackState): boolean =>
  playback.loadingPromptId === prompt.id || (playback.audioLoading && playback.playbackPromptId === prompt.id)

const isPromptPlaybackPlaying = (prompt: SunoPrompt, playback: PromptLibraryPlaybackState): boolean =>
  Boolean(
    playback.playingTrack
    && playback.playbackPromptId === prompt.id
    && playback.playbackTracks.some((track) => track.id === playback.playingTrack?.id)
    && playback.audioPlaying
  )

export const PromptLibraryTableRow = ({
  actionMenuId,
  actionMenuPosition,
  audioLoading,
  audioPlaying,
  isCurrentPromptVariant = false,
  isExpanded = false,
  isPoolVariant = false,
  loadingPromptId,
  onCloseActionMenu,
  onCopyText,
  onDeletePrompt,
  onDissolvePromptPool,
  onEditPrompt,
  onOpenPromptPoolEditor,
  onOpenSimilarity,
  onOpenTracks,
  onPlayPromptTracks,
  onRemovePromptPoolVariant,
  onSetPromptPoolCanonical,
  onToggleActionMenu,
  onTogglePromptPoolExpansion,
  playbackPromptId,
  playbackTracks,
  playingTrack,
  pool,
  prompt
}: PromptLibraryTableRowProps) => {
  const playback = { audioLoading, audioPlaying, loadingPromptId, playbackPromptId, playbackTracks, playingTrack }
  const isCanonical = pool?.canonical_suno_prompt_id === prompt.id || prompt.pool?.canonical_suno_prompt_id === prompt.id
  const currentActionMenuId = `${isPoolVariant ? 'pool-variant' : 'prompt'}-${prompt.id}`
  const actionMenuOpen = actionMenuId === currentActionMenuId
  const playbackLoading = isPromptPlaybackLoading(prompt, playback)
  const playbackPlaying = isPromptPlaybackPlaying(prompt, playback)
  const rowClassName = cx(
    isPoolVariant && styles['prompt-pool-variant-row'],
    isExpanded && styles['prompt-pool-expanded-row'],
    isCurrentPromptVariant && styles['prompt-pool-current-row']
  ) || undefined

  return (
    <tr className={rowClassName} key={isPoolVariant ? `pool-variant-${prompt.id}` : prompt.id}>
      <td className="col-icon" data-label="">
        {(prompt.linked_track_count ?? 0) > 0 ? (
          <IconButton
            disabled={playbackLoading}
            icon={playbackLoading ? (
              <span className="track-status-spinner" />
            ) : playbackPlaying ? (
              <Pause size={15} />
            ) : (
              <Play size={15} />
            )}
            iconSize="play"
            label={`Play tracks for ${prompt.title}`}
            loading={playbackLoading}
            onClick={() => void onPlayPromptTracks(prompt)}
            title={playbackLoading ? 'Loading' : playbackPlaying ? 'Pause' : 'Play linked tracks'}
          />
        ) : null}
      </td>
      <td className={styles['prompt-title-cell']} data-label="">
        {(isPoolVariant || !prompt.pool) && (
          <strong>
            {prompt.title}
            {isPoolVariant && isCanonical && <Crown className={styles['prompt-canonical-icon']} size={14} />}
          </strong>
        )}
        {!isPoolVariant && prompt.pool && (
          <button
            aria-expanded={isExpanded}
            className={styles['prompt-pool-inline']}
            onClick={() => onTogglePromptPoolExpansion(prompt.pool!.id, prompt.id)}
            type="button"
          >
            <ChevronDown aria-hidden="true" className={isExpanded ? cx(styles['prompt-pool-chevron'], 'expanded') : cx(styles['prompt-pool-chevron'])} size={18} />
            <Layers size={16} />
            <span className={styles['prompt-pool-inline-name']}>{prompt.pool.name}</span>
            <span className={styles['prompt-pool-inline-count']}>{prompt.pool_variant_count}</span>
          </button>
        )}
        {prompt.description && <ClampedText className={cx(styles['prompt-description-text'])} lines={2}>{prompt.description}</ClampedText>}
      </td>
      <td className={styles['prompt-groups-cell']} data-label="Groups">
        {prompt.groups.length > 0 ? (
          <div className={styles['prompt-group-chips']}>
            {prompt.groups.map((group) => (
              <span className={styles['prompt-group-chip']} key={group.id}>{group.name}</span>
            ))}
          </div>
        ) : (
          <span className="muted">No groups</span>
        )}
      </td>
      <td className={styles['prompt-text-cell']} data-label="Style">
        <div className={styles['prompt-text-cell-inner']}>
          <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{prompt.style_prompt}</code></ClampedText>
          <IconButton
            icon={<Copy size={15} />}
            label={`Copy style prompt ${prompt.title}`}
            onClick={() => void onCopyText(prompt.style_prompt, 'style prompt')}
            title="Copy style prompt"
          />
        </div>
      </td>
      <td className={cx(styles['prompt-exclude-cell'], styles['prompt-text-cell'])} data-label="Exclude">
        {prompt.exclude_prompt ? (
          <div className={styles['prompt-text-cell-inner']}>
            <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{prompt.exclude_prompt}</code></ClampedText>
            <IconButton
              icon={<Copy size={15} />}
              label={`Copy exclude prompt ${prompt.title}`}
              onClick={() => void onCopyText(prompt.exclude_prompt ?? '', 'exclude prompt')}
              title="Copy exclude prompt"
            />
          </div>
        ) : (
          <span className="muted">-</span>
        )}
      </td>
      <td className={styles['prompt-tracks-cell']} data-label="Tracks">
        <button
          className={styles['prompt-track-count-button']}
          onClick={() => onOpenTracks(prompt)}
          type="button"
        >
          <Link2 size={15} />
          <span>{prompt.linked_track_count ?? 0}</span>
        </button>
      </td>
      <td className={styles['prompt-updated-cell']} data-label="Updated">
        {new Date(prompt.updated_at).toLocaleDateString()}
      </td>
      <td className={styles['prompt-actions-cell']} data-label="Actions">
        <div
          className={styles['prompt-action-menu-wrap']}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <IconButton
            active={actionMenuOpen}
            aria-expanded={actionMenuOpen}
            aria-haspopup="menu"
            className="prompt-action-menu-trigger"
            icon={<MoreVertical size={15} />}
            label={`Open actions for ${prompt.title}`}
            onClick={(event) => onToggleActionMenu(event, currentActionMenuId)}
            title="Actions"
          />
          {actionMenuOpen && (
            <div
              className={cx(styles['prompt-action-menu'], actionMenuPosition?.placement === 'above' && 'opens-above')}
              role="menu"
              style={actionMenuPosition ? {
                left: actionMenuPosition.left,
                top: actionMenuPosition.top
              } : undefined}
            >
              <Link
                aria-label={`Generate names from ${prompt.title}`}
                className={styles['prompt-action-menu-item']}
                onClick={onCloseActionMenu}
                role="menuitem"
                to={`/admin/tools/track-name-ideas?namePromptId=${encodeURIComponent(prompt.id)}`}
              >
                <Wand2 size={15} />
                <span>Generate names</span>
              </Link>
              <Link
                aria-label={`Evolve ${prompt.title}`}
                className={styles['prompt-action-menu-item']}
                onClick={onCloseActionMenu}
                role="menuitem"
                to={`/admin/tools/suno-style-prompts?basePromptId=${encodeURIComponent(prompt.id)}`}
              >
                <Sparkles size={15} />
                <span>Evolve</span>
              </Link>
              {!isPoolVariant && (
                <button
                  aria-label={`Find prompts similar to ${prompt.title}`}
                  className={styles['prompt-action-menu-item']}
                  onClick={() => {
                    onCloseActionMenu()
                    onOpenSimilarity(prompt)
                  }}
                  role="menuitem"
                  type="button"
                >
                  <Search size={15} />
                  <span>Find similar</span>
                </button>
              )}
              <button
                aria-label={`Edit ${prompt.title}`}
                className={styles['prompt-action-menu-item']}
                onClick={() => {
                  onCloseActionMenu()
                  onEditPrompt(prompt)
                }}
                role="menuitem"
                type="button"
              >
                <Edit3 size={15} />
                <span>Edit</span>
              </button>
              {!isPoolVariant && prompt.pool && (
                <button
                  aria-label={`Edit pool ${prompt.pool.name}`}
                  className={styles['prompt-action-menu-item']}
                  onClick={() => {
                    onCloseActionMenu()
                    void onOpenPromptPoolEditor(prompt.pool!.id)
                  }}
                  role="menuitem"
                  type="button"
                >
                  <Layers size={15} />
                  <span>Edit pool</span>
                </button>
              )}
              {pool && !isCanonical && (
                <button
                  aria-label={`Set ${prompt.title} as canonical`}
                  className={styles['prompt-action-menu-item']}
                  onClick={() => {
                    onCloseActionMenu()
                    void onSetPromptPoolCanonical(pool, prompt.id)
                  }}
                  role="menuitem"
                  type="button"
                >
                  <Check size={15} />
                  <span>Set canonical</span>
                </button>
              )}
              {pool && (
                <button
                  aria-label={`Remove ${prompt.title} from pool`}
                  className={styles['prompt-action-menu-item']}
                  disabled={pool.variants.length <= 2}
                  onClick={() => {
                    onCloseActionMenu()
                    void onRemovePromptPoolVariant(pool, prompt.id)
                  }}
                  role="menuitem"
                  type="button"
                >
                  <Unlink size={15} />
                  <span>Remove from pool</span>
                </button>
              )}
              {!isPoolVariant && prompt.pool && (
                <button
                  aria-label={`Dissolve pool ${prompt.pool.name}`}
                  className={cx(styles['prompt-action-menu-item'], 'danger')}
                  onClick={() => {
                    onCloseActionMenu()
                    void onDissolvePromptPool(prompt.pool!.id)
                  }}
                  role="menuitem"
                  type="button"
                >
                  <Trash2 size={15} />
                  <span>Dissolve pool</span>
                </button>
              )}
              <button
                aria-label={`Delete ${prompt.title}`}
                className={cx(styles['prompt-action-menu-item'], 'danger')}
                onClick={() => {
                  onCloseActionMenu()
                  onDeletePrompt(prompt)
                }}
                role="menuitem"
                type="button"
              >
                <Trash2 size={15} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

type PromptLibraryMobileCardProps = PromptLibraryItemActions & PromptLibraryPlaybackState & PromptLibraryItemOptions & {
  expandedPromptPoolAnchorPromptIds: Record<string, string | undefined>
  expandedPromptPoolIds: Set<string>
  prompt: SunoPrompt
}

export const PromptLibraryMobileCard = ({
  audioLoading,
  audioPlaying,
  expandedPromptPoolAnchorPromptIds,
  expandedPromptPoolIds,
  isPoolVariant = false,
  loadingPromptId,
  onCopyText,
  onDeletePrompt,
  onDissolvePromptPool: _onDissolvePromptPool,
  onEditPrompt,
  onOpenPromptPoolEditor,
  onOpenSimilarity,
  onOpenTracks,
  onPlayPromptTracks,
  onRemovePromptPoolVariant,
  onSetPromptPoolCanonical,
  onTogglePromptPoolExpansion,
  playbackPromptId,
  playbackTracks,
  playingTrack,
  pool,
  prompt
}: PromptLibraryMobileCardProps) => {
  const poolId = prompt.pool?.id
  const isExpanded = Boolean(poolId && expandedPromptPoolIds.has(poolId) && expandedPromptPoolAnchorPromptIds[poolId] === prompt.id)
  const isCanonical = pool?.canonical_suno_prompt_id === prompt.id || prompt.pool?.canonical_suno_prompt_id === prompt.id
  const playback = { audioLoading, audioPlaying, loadingPromptId, playbackPromptId, playbackTracks, playingTrack }
  const playbackLoading = isPromptPlaybackLoading(prompt, playback)
  const playbackPlaying = isPromptPlaybackPlaying(prompt, playback)

  return (
    <article
      className={cx(styles['prompt-mobile-card'], isPoolVariant && styles['prompt-mobile-card-variant'])}
      key={isPoolVariant ? `mobile-pool-variant-${prompt.id}` : `mobile-${prompt.id}`}
    >
      <div className={styles['prompt-mobile-card-header']}>
        <div className={styles['prompt-mobile-title']}>
          <strong>
            {prompt.title}
            {isCanonical && <Crown className={styles['prompt-canonical-icon']} size={14} />}
          </strong>
          {prompt.description && <span>{prompt.description}</span>}
        </div>
        {(prompt.linked_track_count ?? 0) > 0 && (
          <IconButton
            disabled={playbackLoading}
            icon={playbackLoading ? <span className="track-status-spinner" /> : playbackPlaying ? <Pause size={15} /> : <Play size={15} />}
            iconSize="play"
            label={playbackPlaying ? `Pause tracks for ${prompt.title}` : `Play tracks for ${prompt.title}`}
            loading={playbackLoading}
            onClick={() => void onPlayPromptTracks(prompt)}
            title={playbackLoading ? 'Loading' : playbackPlaying ? 'Pause' : 'Play linked tracks'}
          />
        )}
      </div>

      {!isPoolVariant && prompt.pool && (
        <button
          aria-expanded={isExpanded}
          className={styles['prompt-mobile-pool-button']}
          onClick={() => onTogglePromptPoolExpansion(prompt.pool!.id, prompt.id)}
          type="button"
        >
          <ChevronDown aria-hidden="true" className={isExpanded ? cx(styles['prompt-pool-chevron'], 'expanded') : cx(styles['prompt-pool-chevron'])} size={18} />
          <Layers size={15} />
          <span>{prompt.pool.name}</span>
          <Pill>{prompt.pool_variant_count}</Pill>
        </button>
      )}

      <div className={styles['prompt-group-chips']}>
        {prompt.groups.length > 0 ? (
          prompt.groups.map((group) => (
            <span className={styles['prompt-group-chip']} key={group.id}>{group.name}</span>
          ))
        ) : (
          <span className={styles['prompt-mobile-muted']}>No groups</span>
        )}
      </div>

      <div className={styles['prompt-mobile-preview-block']}>
        <div className={styles['prompt-mobile-section-label']}>Style</div>
        <div className={styles['prompt-text-cell-inner']}>
          <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{prompt.style_prompt}</code></ClampedText>
          <IconButton
            icon={<Copy size={15} />}
            label={`Copy style prompt ${prompt.title}`}
            onClick={() => void onCopyText(prompt.style_prompt, 'style prompt')}
            title="Copy style prompt"
          />
        </div>
      </div>

      {prompt.exclude_prompt && (
        <div className={styles['prompt-mobile-preview-block']}>
          <div className={styles['prompt-mobile-section-label']}>Exclude</div>
          <div className={styles['prompt-text-cell-inner']}>
            <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{prompt.exclude_prompt}</code></ClampedText>
            <IconButton
              icon={<Copy size={15} />}
              label={`Copy exclude prompt ${prompt.title}`}
              onClick={() => void onCopyText(prompt.exclude_prompt ?? '', 'exclude prompt')}
              title="Copy exclude prompt"
            />
          </div>
        </div>
      )}

      <div className={styles['prompt-mobile-meta']}>
        <span>{new Date(prompt.updated_at).toLocaleDateString()}</span>
        <button
          className={styles['prompt-track-count-button']}
          onClick={() => onOpenTracks(prompt)}
          type="button"
        >
          <Link2 size={15} />
          <span>{prompt.linked_track_count ?? 0}</span>
        </button>
      </div>

      <RowActions className={styles['prompt-mobile-actions'] ?? ''} mobileStack>
        <Link
          aria-label={`Generate names from ${prompt.title}`}
          className={styles['prompt-mobile-action-link']}
          to={`/admin/tools/track-name-ideas?namePromptId=${encodeURIComponent(prompt.id)}`}
        >
          <Wand2 size={15} />
          <span>Names</span>
        </Link>
        <Link
          aria-label={`Evolve ${prompt.title}`}
          className={styles['prompt-mobile-action-link']}
          to={`/admin/tools/suno-style-prompts?basePromptId=${encodeURIComponent(prompt.id)}`}
        >
          <Sparkles size={15} />
          <span>Evolve</span>
        </Link>
        {!isPoolVariant && (
          <IconButton
            icon={<Search size={15} />}
            label={`Find prompts similar to ${prompt.title}`}
            onClick={() => onOpenSimilarity(prompt)}
            title="Similar"
          />
        )}
        <IconButton
          icon={<Edit3 size={15} />}
          label={`Edit ${prompt.title}`}
          onClick={() => onEditPrompt(prompt)}
          title="Edit"
        />
        {!isPoolVariant && prompt.pool && (
          <IconButton
            icon={<Layers size={15} />}
            label={`Edit pool ${prompt.pool.name}`}
            onClick={() => void onOpenPromptPoolEditor(prompt.pool!.id)}
            title="Edit pool"
          />
        )}
        {pool && !isCanonical && (
          <IconButton
            icon={<Check size={15} />}
            label={`Set ${prompt.title} as canonical`}
            onClick={() => void onSetPromptPoolCanonical(pool, prompt.id)}
            title="Set canonical"
          />
        )}
        {pool && (
          <IconButton
            disabled={pool.variants.length <= 2}
            icon={<Unlink size={15} />}
            label={`Remove ${prompt.title} from pool`}
            onClick={() => void onRemovePromptPoolVariant(pool, prompt.id)}
            title="Remove from pool"
          />
        )}
        <IconButton
          icon={<Trash2 size={15} />}
          label={`Delete ${prompt.title}`}
          onClick={() => onDeletePrompt(prompt)}
          title="Delete"
          tone="danger"
        />
      </RowActions>
    </article>
  )
}
